import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface ColorEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (color: string, type: 'primary' | 'secondary' | 'accent' | 'neutral') => void;
  initialColor: string;
  initialType: 'primary' | 'secondary' | 'accent' | 'neutral';
  isEditing: boolean;
}

export const ColorEditModal: React.FC<ColorEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialColor,
  initialType,
  isEditing,
}) => {
  const [color, setColor] = useState(initialColor);
  const [type, setType] = useState(initialType);

  useEffect(() => {
    if (isOpen) {
      setColor(initialColor);
      setType(initialType);
    }
  }, [isOpen, initialColor, initialType]);

  const handleSave = () => {
    onSave(color, type);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Color' : 'Add New Color'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Color Preview */}
          <div className="text-center">
            <div
              className="w-20 h-20 rounded-xl border border-gray-200 shadow-sm mx-auto mb-4"
              style={{ backgroundColor: color }}
            />
            <div className="text-sm font-mono text-gray-600">{color.toUpperCase()}</div>
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Choose Color
            </label>
            <div className="space-y-3">
              {/* HTML Color Picker */}
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-full h-12 rounded-lg border border-gray-300 cursor-pointer"
              />
              
              {/* Manual Hex Input */}
              <input
                type="text"
                value={color}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.match(/^#[0-9A-Fa-f]{0,6}$/)) {
                    setColor(value);
                  }
                }}
                placeholder="#000000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Color Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Color Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'primary' as const, label: 'Primary', color: 'bg-blue-100 text-blue-700' },
                { value: 'secondary' as const, label: 'Secondary', color: 'bg-purple-100 text-purple-700' },
                { value: 'accent' as const, label: 'Accent', color: 'bg-green-100 text-green-700' },
                { value: 'neutral' as const, label: 'Neutral', color: 'bg-gray-100 text-gray-700' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setType(option.value)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    type === option.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${option.color} mb-2`}>
                    {option.label.toUpperCase()}
                  </div>
                  <div className="text-sm text-gray-600">{option.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Color Presets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Popular Colors
            </label>
            <div className="grid grid-cols-8 gap-2">
              {[
                '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
                '#800000', '#008000', '#000080', '#808000', '#800080', '#008080', '#C0C0C0', '#808080',
                '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'
              ].map((presetColor) => (
                <button
                  key={presetColor}
                  onClick={() => setColor(presetColor)}
                  className={`w-8 h-8 rounded-lg border border-black transition-all ${
                    color === presetColor ? 'border-2 border-primary-500' : 'hover:border-gray-600'
                  }`}
                  style={{ 
                    backgroundColor: presetColor
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            {isEditing ? 'Update Color' : 'Add Color'}
          </button>
        </div>
      </div>
    </div>
  );
}; 